<script setup>
import { ref, computed, onMounted, nextTick, watch, onBeforeUnmount } from 'vue'

const emit = defineEmits(['update:modelValue'])
const props = defineProps(['modelValue', 'options'])

const toggleContainer = ref(null)
const buttonRefs = ref([])
const sliderWidth = ref(0)
const sliderOffset = ref(0)
const calculationFailed = ref(false)
let intersectionObserver = null

const active = computed(() => props.options.findIndex((option) => option.slug === props.modelValue))

// Slider offset
const calculateOffset = async (retryCount = 0) => {
	await nextTick()

	if (!toggleContainer.value || buttonRefs.value.length === 0) {
		// Retry
		if (retryCount < 3) {
			setTimeout(() => calculateOffset(retryCount + 1), 50 * (retryCount + 1))
		}
		return
	}

	const containerRect = toggleContainer.value.getBoundingClientRect()
	const activeIndex = active.value

	// Container size
	if (containerRect.width === 0 || containerRect.height === 0) {
		if (retryCount < 3) {
			setTimeout(() => calculateOffset(retryCount + 1), 50 * (retryCount + 1))
		}
		return
	}

	// Visibility
	const computedStyle = window.getComputedStyle(toggleContainer.value)
	if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
		if (retryCount < 3) {
			setTimeout(() => calculateOffset(retryCount + 1), 100 * (retryCount + 1))
		}
		return
	}

	if (activeIndex >= 0 && activeIndex < buttonRefs.value.length) {
		const activeButton = buttonRefs.value[activeIndex]
		if (activeButton) {
			const buttonRect = activeButton.getBoundingClientRect()

			// Button size
			if (buttonRect.width === 0 || buttonRect.height === 0) {
				if (retryCount < 3) {
					setTimeout(() => calculateOffset(retryCount + 1), 50 * (retryCount + 1))
				}
				return
			}

			const containerLeft = containerRect.left
			const buttonLeft = buttonRect.left

			// Slider offset
			sliderOffset.value = buttonLeft - containerLeft - 4 // Padding
			sliderWidth.value = buttonRect.width
			calculationFailed.value = false
		}
	} else {
		if (buttonRefs.value.length > 0 && retryCount >= 2) {
			const firstButton = buttonRefs.value[0]
			if (firstButton) {
				const buttonRect = firstButton.getBoundingClientRect()
				const containerRect = toggleContainer.value.getBoundingClientRect()

				if (buttonRect.width > 0 && containerRect.width > 0) {
					sliderOffset.value = 4
					sliderWidth.value = buttonRect.width
					calculationFailed.value = false
				}
			}
		}
	}
}

watch(active, calculateOffset, { immediate: true })

watch(
	() => props.options,
	() => {
		if (props.options && props.options.length > 0) {
			calculateOffset()
		}
	},
	{ immediate: true },
)

onMounted(() => {
	setTimeout(() => {
		calculateOffset()
	}, 100)
	window.addEventListener('resize', calculateOffset)

	if (window.IntersectionObserver) {
		intersectionObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && sliderWidth.value === 0) {
					calculateOffset()
				}
			})
		})
		toggleContainer.value && intersectionObserver.observe(toggleContainer.value)
	}
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', calculateOffset)
	if (intersectionObserver) {
		intersectionObserver.disconnect()
	}
})
</script>

<template>
	<div ref="toggleContainer" class="relative flex w-fit select-none gap-1 rounded-md bg-snk-blue-500 p-1 text-snk-light-500">
		<div
			v-if="!calculationFailed && sliderWidth > 0"
			class="absolute z-10 h-[calc(100%-8px)] rounded-md bg-white transition-all duration-200 ease-in-out"
			:style="{
				transform: `translateX(${sliderOffset}px)`,
				width: `${sliderWidth}px`,
			}"></div>
		<button :ref="(el) => (buttonRefs[i] = el)" :class="['relative z-10 min-h-8 px-3 py-1 text-sm font-semibold uppercase leading-tight transition-colors duration-200', { 'text-black': active == i }, { 'opacity-50': option.disabled }]" v-for="(option, i) in props.options" @click="emit('update:modelValue', option.slug)" :title="option?.title" :tabindex="i" role="button" :aria-pressed="active == i" :disabled="option.disabled">
			<i :class="option.icon" v-if="option.icon"></i>
			<template v-else>
				<span class="hidden lg:block">{{ option.name }}</span>
				<span class="lg:hidden">{{ option.name_short ?? option.name }}</span>
			</template>
		</button>
	</div>
</template>
